import { useState, useCallback } from "react";
import { educationService } from "../apis/Education/educationService";

export const useEducation = () => {
  // State for categories
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState(null);

  // State for courses
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [coursesError, setCoursesError] = useState(null);

  // State for current course
  const [currentCourse, setCurrentCourse] = useState(null);
  const [currentCourseLoading, setCurrentCourseLoading] = useState(false);
  const [currentCourseError, setCurrentCourseError] = useState(null);

  // State for user's enrolled courses
  const [myCourses, setMyCourses] = useState([]);
  const [myCoursesLoading, setMyCoursesLoading] = useState(false);
  const [myCoursesError, setMyCoursesError] = useState(null);

  // State for bookmarks
  const [bookmarks, setBookmarks] = useState([]);
  const [bookmarksLoading, setBookmarksLoading] = useState(false);
  const [bookmarksError, setBookmarksError] = useState(null);

  // State for keywords
  const [keywords, setKeywords] = useState([]);
  const [keywordsLoading, setKeywordsLoading] = useState(false);
  const [keywordsError, setKeywordsError] = useState(null);

  // State for class progress
  const [classProgress, setClassProgress] = useState([]);
  const [classProgressLoading, setClassProgressLoading] = useState(false);
  const [classProgressError, setClassProgressError] = useState(null);

  // State for classes and contents
  const [classes, setClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [classesError, setClassesError] = useState(null);

  const [currentClass, setCurrentClass] = useState(null);
  const [currentClassLoading, setCurrentClassLoading] = useState(false);
  const [currentClassError, setCurrentClassError] = useState(null);

  const [contents, setContents] = useState([]);
  const [contentsLoading, setContentsLoading] = useState(false);
  const [contentsError, setContentsError] = useState(null);

  const [contentProgress, setContentProgress] = useState([]);
  const [contentProgressLoading, setContentProgressLoading] = useState([]);
  const [contentProgressError, setContentProgressError] = useState([]);

  const [favoriteClasses, setFavoriteClasses] = useState([]);
  const [favoriteClassesLoading, setFavoriteClassesLoading] = useState(false);
  const [favoriteClassesError, setFavoriteClassesError] = useState(null);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    setCategoriesLoading(true);
    setCategoriesError(null);

    try {
      const data = await educationService.getCategories();
      setCategories(data);
    } catch (error) {
      setCategoriesError(error.message);
      console.error("Failed to fetch categories:", error);
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  // Fetch courses
  const fetchCourses = useCallback(async (filters = {}) => {
    setCoursesLoading(true);
    setCoursesError(null);

    try {
      const data = await educationService.getCourses(filters);
      setCourses(data);
    } catch (error) {
      setCoursesError(error.message);
      console.error("Failed to fetch courses:", error);
    } finally {
      setCoursesLoading(false);
    }
  }, []);

  // Fetch featured courses
  const fetchFeaturedCourses = useCallback(async () => {
    setCoursesLoading(true);
    setCoursesError(null);

    try {
      const data = await educationService.getFeaturedCourses();
      setCourses(data);
    } catch (error) {
      setCoursesError(error.message);
      console.error("Failed to fetch featured courses:", error);
    } finally {
      setCoursesLoading(false);
    }
  }, []);

  // Fetch courses by category
  const fetchCoursesByCategory = useCallback(async () => {
    setCoursesLoading(true);
    setCoursesError(null);

    try {
      const data = await educationService.getCoursesByCategory();
      return data; // This return is important since we're not storing in state directly
    } catch (error) {
      setCoursesError(error.message);
      console.error("Failed to fetch courses by category:", error);
      return [];
    } finally {
      setCoursesLoading(false);
    }
  }, []);

  // Fetch course by ID
  const fetchCourse = useCallback(async (courseId) => {
    setCurrentCourseLoading(true);
    setCurrentCourseError(null);

    try {
      const data = await educationService.getCourseById(courseId);
      setCurrentCourse(data);
      return data;
    } catch (error) {
      setCurrentCourseError(error.message);
      console.error(`Failed to fetch course ${courseId}:`, error);
      return null;
    } finally {
      setCurrentCourseLoading(false);
    }
  }, []);

  // Fetch user's enrolled courses
  const fetchMyCourses = useCallback(async (filters = {}) => {
    setMyCoursesLoading(true);
    setMyCoursesError(null);

    try {
      const data = await educationService.getMyCourses(filters);
      setMyCourses(data);
      return data;
    } catch (error) {
      setMyCoursesError(error.message);
      console.error("Failed to fetch enrolled courses:", error);
      return [];
    } finally {
      setMyCoursesLoading(false);
    }
  }, []);

  // Fetch user's favorite courses
  const fetchFavoriteCourses = useCallback(async () => {
    setMyCoursesLoading(true);
    setMyCoursesError(null);

    try {
      const data = await educationService.getFavoriteCourses();
      setMyCourses(data);
      return data;
    } catch (error) {
      setMyCoursesError(error.message);
      console.error("Failed to fetch favorite courses:", error);
      return [];
    } finally {
      setMyCoursesLoading(false);
    }
  }, []);

  // Fetch in-progress courses
  const fetchInProgressCourses = useCallback(async () => {
    setMyCoursesLoading(true);
    setMyCoursesError(null);

    try {
      const data = await educationService.getInProgressCourses();
      setMyCourses(data);
      return data;
    } catch (error) {
      setMyCoursesError(error.message);
      console.error("Failed to fetch in-progress courses:", error);
      return [];
    } finally {
      setMyCoursesLoading(false);
    }
  }, []);

  // Fetch completed courses
  const fetchCompletedCourses = useCallback(async () => {
    setMyCoursesLoading(true);
    setMyCoursesError(null);

    try {
      const data = await educationService.getCompletedCourses();
      setMyCourses(data);
      return data;
    } catch (error) {
      setMyCoursesError(error.message);
      console.error("Failed to fetch completed courses:", error);
      return [];
    } finally {
      setMyCoursesLoading(false);
    }
  }, []);

  // Enroll in a course
  const enrollInCourse = useCallback(
    async (courseId) => {
      try {
        const data = await educationService.enrollInCourse(courseId);
        // Refresh my courses list
        fetchMyCourses();
        return data;
      } catch (error) {
        console.error(`Failed to enroll in course ${courseId}:`, error);
        throw error;
      }
    },
    [fetchMyCourses]
  );

  // Unenroll from a course
  const unenrollFromCourse = useCallback(
    async (myCourseId) => {
      try {
        await educationService.unenrollFromCourse(myCourseId);
        // Refresh my courses list
        fetchMyCourses();
        return true;
      } catch (error) {
        console.error(`Failed to unenroll from course ${myCourseId}:`, error);
        throw error;
      }
    },
    [fetchMyCourses]
  );

  // Toggle course favorite status
  const toggleFavoriteCourse = useCallback(async (myCourseId) => {
    try {
      const response = await educationService.toggleFavoriteCourse(myCourseId);

      // Update the course in myCourses state
      setMyCourses((prevCourses) =>
        prevCourses.map((course) =>
          course.id === myCourseId
            ? { ...course, is_favorite: response.is_favorite }
            : course
        )
      );

      return response;
    } catch (error) {
      console.error(
        `Failed to toggle favorite for course ${myCourseId}:`,
        error
      );
      throw error;
    }
  }, []);

  const fetchClassProgress = useCallback(async (filters = {}) => {
    setClassProgressLoading(true);
    setClassProgressError(null);

    try {
      const data = await educationService.getClassProgress(filters);
      setClassProgress(data.results || []);
      return data;
    } catch (error) {
      setClassProgressError(error.message);
      console.error("Failed to fetch class progress:", error);
      return { results: [] };
    } finally {
      setClassProgressLoading(false);
    }
  }, []);

  // Create class progress
  const createClassProgress = useCallback(async (progressData) => {
    try {
      const data = await educationService.createClassProgress(progressData);
      return data;
    } catch (error) {
      console.error("Failed to create class progress:", error);
      throw error;
    }
  }, []);

  // Update class progress status
  const updateClassProgress = useCallback(async (progressId, status) => {
    try {
      const response = await educationService.updateClassProgressStatus(
        progressId,
        status
      );
      return response;
    } catch (error) {
      console.error(`Failed to update class progress ${progressId}:`, error);
      throw error;
    }
  }, []);

  const fetchFavoriteClasses = useCallback(async () => {
    setFavoriteClassesLoading(true);
    setFavoriteClassesError(null);

    try {
      const data = await educationService.getFavoriteClasses();

      setFavoriteClasses(data || []);
      return data;
    } catch (error) {
      setFavoriteClassesError(error.message);
      console.error("Failed to fetch favorite classes:", error);
      return [];
    } finally {
      setFavoriteClassesLoading(false);
    }
  }, []);

  const toggleFavoriteClass = useCallback(
    async (progressId) => {
      try {
        const response = await educationService.toggleFavoriteClass(progressId);

        // Update favorite classes list if it's currently loaded
        if (favoriteClasses.length > 0) {
          if (response.is_favorite) {
            // Class was favorited - fetch the updated class progress and add to favorites
            const updatedProgress = await educationService.getClassProgress({
              id: progressId,
            });
            if (updatedProgress.results && updatedProgress.results.length > 0) {
              setFavoriteClasses((prev) => [
                ...prev,
                updatedProgress.results[0],
              ]);
            }
          } else {
            // Class was unfavorited - remove from favorites list
            setFavoriteClasses((prev) =>
              prev.filter((cls) => cls.id !== progressId)
            );
          }
        }

        return response;
      } catch (error) {
        console.error(
          `Failed to favorite class progress ${progressId}:`,
          error
        );
        throw error;
      }
    },
    [favoriteClasses]
  );

  // Get classes for a course
  const getClassesByCourse = useCallback(async (courseId) => {
    setClassesLoading(true);
    setClassesError(null);

    try {
      const data = await educationService.getClassesByCourse(courseId);
      setClasses(data);
      return data;
    } catch (error) {
      setClassesError(error.message);
      console.error(`Failed to fetch classes for course ${courseId}:`, error);
      return [];
    } finally {
      setClassesLoading(false);
    }
  }, []);

  // Get class by ID
  const getClassById = useCallback(async (classId) => {
    setCurrentClassLoading(true);
    setCurrentClassError(null);

    try {
      const data = await educationService.getClassById(classId);
      setCurrentClass(data);
      return data;
    } catch (error) {
      setCurrentClassError(error.message);
      console.error(`Failed to fetch class ${classId}:`, error);
      return null;
    } finally {
      setCurrentClassLoading(false);
    }
  }, []);

  // Get contents for a class
  const getContentsByClass = useCallback(async (classId) => {
    setContentsLoading(true);
    setContentsError(null);

    try {
      const data = await educationService.getContentsByClass(classId);
      setContents(data);
      return data;
    } catch (error) {
      setContentsError(error.message);
      console.error(`Failed to fetch contents for class ${classId}:`, error);
      return [];
    } finally {
      setContentsLoading(false);
    }
  }, []);

  // Get content by ID
  const getContentById = useCallback(async (contentId) => {
    try {
      const data = await educationService.getContentById(contentId);
      return data;
    } catch (error) {
      console.error(`Failed to fetch content ${contentId}:`, error);
      return null;
    }
  }, []);

  // Update content position (for videos)
  const updateContentPosition = useCallback(async (progressId, position) => {
    try {
      const response = await educationService.updateContentPosition(
        progressId,
        position
      );
      return response;
    } catch (error) {
      console.error(
        `Failed to update content position for ${progressId}:`,
        error
      );
      throw error;
    }
  }, []);

  const fetchContentProgress = useCallback(async (filters = {}) => {
    setContentProgressLoading(true);
    setContentProgressError(null);
    try {
      const data = await educationService.getContentProgress(filters);
      setContentProgress(data);
      return data;
    } catch (error) {
      console.error(`Failed to get content progress.`, error);
      throw error;
    } finally {
      setContentProgressLoading(false);
    }
  }, []);

  const createContentProgress = useCallback(async (progressData) => {
    setContentProgressLoading(true);
    setContentProgressError(null);
    try {
      const data = await educationService.createContentProgress(progressData);
      setContentProgress(data);
      return data;
    } catch (error) {
      console.error(`Failed to create new content progress.`, error);
      throw error;
    } finally {
      setContentProgressLoading(false);
    }
  }, []);

  // Mark content as completed
  const markContentCompleted = useCallback(async (progressId) => {
    try {
      const response = await educationService.markContentCompleted(progressId);
      return response;
    } catch (error) {
      console.error(
        `Failed to mark content completed for ${progressId}:`,
        error
      );
      throw error;
    }
  }, []);

  // Submit quiz results
  const submitQuizResult = useCallback(async (quizData) => {
    try {
      const response = await educationService.submitQuizResult(quizData);
      return response;
    } catch (error) {
      console.error("Failed to submit quiz result:", error);
      throw error;
    }
  }, []);

  // Fetch bookmarks for a class
  const fetchBookmarks = useCallback(async (classId) => {
    setBookmarksLoading(true);
    setBookmarksError(null);

    try {
      const data = await educationService.getBookmarks({ edu_class: classId });
      setBookmarks(data);
      return data;
    } catch (error) {
      setBookmarksError(error.message);
      console.error("Failed to fetch bookmarks:", error);
      return [];
    } finally {
      setBookmarksLoading(false);
    }
  }, []);

  // Create a new bookmark
  const createBookmark = useCallback(
    async (bookmarkData) => {
      try {
        const response = await educationService.createBookmark(bookmarkData);
        // Refresh bookmarks if we're on the same class
        if (
          bookmarks.length > 0 &&
          bookmarks[0].edu_class === bookmarkData.edu_class
        ) {
          fetchBookmarks(bookmarkData.edu_class);
        }
        return response;
      } catch (error) {
        console.error("Failed to create bookmark:", error);
        throw error;
      }
    },
    [bookmarks, fetchBookmarks]
  );

  // Delete a bookmark
  const deleteBookmark = useCallback(async (bookmarkId) => {
    try {
      await educationService.deleteBookmark(bookmarkId);
      // Update bookmarks state
      setBookmarks((prevBookmarks) =>
        prevBookmarks.filter((bookmark) => bookmark.id !== bookmarkId)
      );
      return true;
    } catch (error) {
      console.error(`Failed to delete bookmark ${bookmarkId}:`, error);
      throw error;
    }
  }, []);

  // Fetch keywords
  const fetchKeywords = useCallback(async () => {
    setKeywordsLoading(true);
    setKeywordsError(null);

    try {
      const data = await educationService.getKeywords();
      setKeywords(data);
      return data;
    } catch (error) {
      setKeywordsError(error.message);
      console.error("Failed to fetch keywords:", error);
      return [];
    } finally {
      setKeywordsLoading(false);
    }
  }, []);

  const handleBookmarkCreate = (newBookmark) => {
    setBookmarks((prev) => [...prev, newBookmark]);
  };

  const handleBookmarkDelete = (deletedBookmarkId) => {
    setBookmarks((prev) => prev.filter((b) => b.id !== deletedBookmarkId));
  };

  return {
    // Data
    categories,
    courses,
    currentCourse,
    myCourses,
    bookmarks,
    keywords,
    classProgress,
    classes,
    currentClass,
    contents,
    contentProgress,
    favoriteClasses,

    // Loading states
    categoriesLoading,
    coursesLoading,
    currentCourseLoading,
    myCoursesLoading,
    classProgressLoading,
    bookmarksLoading,
    keywordsLoading,
    classesLoading,
    currentClassLoading,
    contentsLoading,
    contentProgressLoading,
    favoriteClassesLoading,

    // Error states
    categoriesError,
    coursesError,
    currentCourseError,
    myCoursesError,
    bookmarksError,
    keywordsError,
    classProgressError,
    classesError,
    currentClassError,
    contentsError,
    contentProgressError,
    favoriteClassesError,

    // Actions
    fetchCategories,
    fetchCourses,
    fetchFeaturedCourses,
    fetchCoursesByCategory,
    fetchCourse,
    fetchMyCourses,
    fetchFavoriteCourses,
    fetchInProgressCourses,
    fetchCompletedCourses,
    fetchClassProgress,
    createClassProgress,
    fetchKeywords,
    enrollInCourse,
    unenrollFromCourse,
    toggleFavoriteCourse,
    toggleFavoriteClass,
    updateClassProgress,
    updateContentPosition,
    markContentCompleted,
    submitQuizResult,
    fetchBookmarks,
    createBookmark,
    deleteBookmark,
    getClassesByCourse,
    getClassById,
    getContentsByClass,
    getContentById,
    handleBookmarkCreate,
    handleBookmarkDelete,
    createContentProgress,
    fetchContentProgress,
    fetchFavoriteClasses,
  };
};
